// src/features/health/components/reports/HealthReportGenerator.tsx
// BP-03-04 Part 3: Health report generation UI

import React, { useState } from 'react';
import { format } from 'date-fns';
import { 
  FileText, 
  Calendar, 
  Download, 
  Loader2,
  CheckCircle,
  Settings,
  Eye
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/core/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useGenerateReport, usePreviewReport } from '../../hooks/useHealthReports';
import { 
  ReportType, 
  ReportTimeframe, 
  ExportFormat,
  reportFormSchema,
  getReportTypeLabel,
  getTimeframeLabel
} from '../../types/reportTypes';
import { HealthReportPreview } from './HealthReportPreview';
import type { z } from 'zod';

interface HealthReportGeneratorProps {
  childId: string;
  onReportGenerated?: (reportId: string) => void;
  className?: string;
}

type FormData = z.infer<typeof reportFormSchema>;

const reportTypes: ReportType[] = [
  'comprehensive',
  'medication',
  'symptom',
  'therapy',
  'progress',
  'provider',
  'custom'
];

const timeframes: ReportTimeframe[] = [
  'week',
  'month',
  'quarter',
  'year',
  'all_time',
  'custom'
];

const exportFormats: ExportFormat[] = ['pdf', 'csv', 'json'];

interface ReportSection {
  id: string;
  label: string;
  description: string;
}

const availableSections: ReportSection[] = [
  { id: 'overview', label: 'Overview Summary', description: 'High-level health summary and key metrics' },
  { id: 'medications', label: 'Medication History', description: 'Medication logs and adherence data' },
  { id: 'symptoms', label: 'Symptom Tracking', description: 'Symptom occurrences and patterns' },
  { id: 'seizures', label: 'Seizure Log', description: 'Detailed seizure records' },
  { id: 'vitals', label: 'Vital Signs', description: 'Temperature, heart rate, weight trends' },
  { id: 'therapy', label: 'Therapy Sessions', description: 'PT, OT, Speech therapy notes' },
  { id: 'appointments', label: 'Appointments', description: 'Medical appointments and visits' },
  { id: 'goals', label: 'Goal Progress', description: 'IEP and therapy goal tracking' },
  { id: 'charts', label: 'Charts & Graphs', description: 'Visual data representations' },
];

export const HealthReportGenerator: React.FC<HealthReportGeneratorProps> = ({
  childId,
  onReportGenerated,
  className
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [generatedReportId, setGeneratedReportId] = useState<string | null>(null);
  
  const generateReport = useGenerateReport(childId);
  const previewReport = usePreviewReport(childId);

  const form = useForm<FormData>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      reportType: 'comprehensive',
      timeframe: 'month',
      title: '',
      includeCharts: true,
      includeSummary: true,
      sections: ['overview', 'medications', 'symptoms'],
      exportFormat: 'pdf'
    }
  });

  const { watch, setValue } = form;
  const selectedTimeframe = watch('timeframe');
  const selectedSections = watch('sections') || [];
  const reportType = watch('reportType');

  const handleSectionToggle = (sectionId: string) => {
    const current = selectedSections;
    if (current.includes(sectionId)) {
      setValue('sections', current.filter(s => s !== sectionId));
    } else {
      setValue('sections', [...current, sectionId]);
    }
  };

  const handlePreview = async () => {
    const values = form.getValues();
    try {
      await previewReport.mutateAsync({
        reportType: values.reportType,
        timeframe: values.timeframe,
        startDate: values.startDate,
        endDate: values.endDate,
        sections: values.sections,
        includeCharts: values.includeCharts,
        includeSummary: values.includeSummary
      });
      setShowPreview(true);
    } catch (error) {
      console.error('Preview failed:', error);
    }
  };

  const handleGenerate = async (data: FormData) => {
    try {
      const result = await generateReport.mutateAsync({
        reportType: data.reportType,
        timeframe: data.timeframe,
        title: data.title || undefined,
        startDate: data.startDate,
        endDate: data.endDate,
        sections: data.sections,
        includeCharts: data.includeCharts,
        includeSummary: data.includeSummary,
        exportFormat: data.exportFormat
      });
      
      setGeneratedReportId(result.id);
      onReportGenerated?.(result.id);
    } catch (error) {
      console.error('Report generation failed:', error);
    }
  };

  const getSuggestedSections = (type: ReportType): string[] => {
    switch (type) {
      case 'medication':
        return ['overview', 'medications'];
      case 'symptom':
        return ['overview', 'symptoms', 'seizures', 'charts'];
      case 'therapy':
        return ['overview', 'therapy', 'goals'];
      case 'progress':
        return ['overview', 'goals', 'therapy', 'charts'];
      case 'provider':
        return ['overview', 'medications', 'symptoms', 'vitals', 'appointments'];
      case 'comprehensive':
      default:
        return ['overview', 'medications', 'symptoms', 'therapy', 'goals', 'charts'];
    }
  };

  const handleReportTypeChange = (type: ReportType) => {
    setValue('reportType', type);
    if (type !== 'custom') {
      setValue('sections', getSuggestedSections(type));
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <FileText className="w-5 h-5 text-sage" />
            Generate Health Report
          </CardTitle>
          <CardDescription>
            Create comprehensive health reports for providers, school, or personal records
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(handleGenerate)} className="space-y-6">
            {/* Report Type Selection */}
            <div className="space-y-3">
              <Label>Report Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {reportTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleReportTypeChange(type)}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-colors',
                      reportType === type
                        ? 'border-sage bg-sage/5'
                        : 'border-gray-200 hover:border-sage/50'
                    )}
                  >
                    <span className="font-medium text-sm text-navy">
                      {getReportTypeLabel(type)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time Period</Label>
                <Select
                  value={selectedTimeframe}
                  onValueChange={(value) => setValue('timeframe', value as ReportTimeframe)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeframes.map((tf) => (
                      <SelectItem key={tf} value={tf}>
                        {getTimeframeLabel(tf)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Export Format</Label>
                <Select
                  value={watch('exportFormat')}
                  onValueChange={(value) => setValue('exportFormat', value as ExportFormat)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {exportFormats.map((fmt) => (
                      <SelectItem key={fmt} value={fmt}>
                        {fmt.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Custom Date Range */}
            {selectedTimeframe === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    {...form.register('startDate')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    {...form.register('endDate')}
                  />
                </div>
              </div>
            )}

            {/* Report Title */}
            <div className="space-y-2">
              <Label>Report Title (Optional)</Label>
              <Input
                placeholder={`${getReportTypeLabel(reportType)} Report - ${format(new Date(), 'MMMM yyyy')}`}
                {...form.register('title')}
              />
            </div>

            {/* Sections Selection */}
            <Accordion type="single" collapsible>
              <AccordionItem value="sections">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    <span>Customize Sections</span>
                    <Badge variant="secondary" className="ml-2">
                      {selectedSections.length} selected
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                    {availableSections.map((section) => (
                      <div
                        key={section.id}
                        className={cn(
                          'flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer',
                          selectedSections.includes(section.id)
                            ? 'border-sage bg-sage/5'
                            : 'border-gray-200 hover:border-gray-300'
                        )}
                        onClick={() => handleSectionToggle(section.id)}
                      >
                        <Checkbox
                          checked={selectedSections.includes(section.id)}
                          onCheckedChange={() => handleSectionToggle(section.id)}
                        />
                        <div>
                          <p className="font-medium text-sm text-navy">{section.label}</p>
                          <p className="text-xs text-warm-gray">{section.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Options */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={watch('includeCharts')}
                  onCheckedChange={(checked) => setValue('includeCharts', !!checked)}
                />
                <span className="text-sm">Include charts and graphs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={watch('includeSummary')}
                  onCheckedChange={(checked) => setValue('includeSummary', !!checked)}
                />
                <span className="text-sm">Include executive summary</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handlePreview}
                disabled={previewReport.isPending}
              >
                {previewReport.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview
              </Button>

              <Button
                type="submit"
                disabled={generateReport.isPending}
                className="bg-sage hover:bg-sage-dark"
              >
                {generateReport.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Success Message */}
      {generatedReportId && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <div>
                <p className="font-medium text-emerald-900">Report Generated Successfully!</p>
                <p className="text-sm text-emerald-700">
                  Your report is ready to download.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  // Download logic would go here
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Preview</DialogTitle>
          </DialogHeader>
          {previewReport.data && (
            <HealthReportPreview report={previewReport.data} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealthReportGenerator;

